import AdvisorTeamMemberData from "@modules/advisory-team/data/team-members.json";
import {
  AdvisorName,
  AdvisorTeamMemberInfo,
} from "@modules/advisory-team/types";
import useStore from "@common/state";
import useMxStore from "@modules/mx/state";
import useAdvisorStore, { store as advisorStore } from "@modules/advisor/state";
import { useMgpStore } from "@modules/mgp/state";
import { usePersistStore as useDocVaultStore } from "@modules/docvault/state";
import { useSignupStore } from "@modules/signup/state";
import { useStore as useMobileStore } from "@mobile/context/state";
import { useWhatsNewStore as useMobileWhatsNewStore } from "@mobile/modules/whats-new/state";
import type { BasicUser } from "@common/types";

const resetPersistedStores = async (isReactNative = false) => {
  advisorStore.getState().reset();
  isReactNative && useSignupStore.getState().reset();
  isReactNative && useMobileStore.getState().reset();
  isReactNative && useMobileWhatsNewStore.getState().reset();
  return Promise.all([
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useMxStore as any).persist.clearStorage(),
    // NOTE: If the store is not used in React Native, it MUST be
    // disabled for ReactNative (!isReactNative).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !isReactNative && (useStore as any).persist.clearStorage(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useSignupStore as any).persist.clearStorage(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useAdvisorStore as any).persist.clearStorage(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useDocVaultStore as any).persist.clearStorage(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (useMgpStore as any).persist.clearStorage(),
  ]);
};

const advisorMeetingSetup = (
  advisor: Forme.Advisor | null | undefined,
  user: Forme.Claims | undefined,
  client: Forme.Client | undefined,
  showAdvisorDetails = true
) => {
  const calendlyUrl =
    (
      AdvisorTeamMemberData[
        advisor?.firstName as AdvisorName
      ] as AdvisorTeamMemberInfo
    )?.calendlyUrl ?? "https://calendly.com/earnedwealth/introduction-45";

  const meetingSetupProps = {
    url: `${calendlyUrl}?hide_gdpr_banner=1&hide_event_type_details=${
      showAdvisorDetails ? 0 : 1
    }`,
    prefill: {
      name: `${client?.firstName} ${client?.lastName}`,
      email: user?.email,
    },
  };

  return meetingSetupProps;
};

const last2DigitYearFormatter = (value: number) =>
  `'${String(value).slice(-2)}`;

const currencyFormatter = (
  value = 0,
  digits = 0,
  compact = false,
  includeSign = false
) =>
  `${value >= 0 ? (includeSign ? "+" : "") : "-"}$${Intl.NumberFormat("en", {
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Math.abs(value))}`;

const percentFormatter = (
  value = 0,
  digits = 0,
  compact = false,
  includeSign = false
) =>
  `${includeSign ? (value >= 0 ? "+" : "-") : ""}${Intl.NumberFormat("en", {
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value * 100)}%`;

// Regex https://stackoverflow.com/questions/46155/how-can-i-validate-an-email-address-in-javascript
const validateEmailFormat = (email: string) => {
  const isValid = email
    .toLowerCase()
    .trim()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
  return isValid;
};

const abbreviateCurrency = (num: number, fixed: number) => {
  if (num === null) {
    return null;
  } // terminate early
  if (num === 0) {
    return "0";
  } // terminate early
  fixed = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
  const b = num.toPrecision(2).split("e"), // get power
    k =
      b.length === 1
        ? 0
        : Math.floor(Math.min(parseFloat(b[1].slice(1)), 14) / 3), // floor at decimals, ceiling at trillions
    c =
      k < 1
        ? num.toFixed(0 + fixed)
        : (num / Math.pow(10, k * 3)).toFixed(1 + fixed), // divide by power
    d = parseFloat(c) < 0 ? c : Math.abs(parseFloat(c)), // enforce -0 is 0
    e = d + ["", "K", "M", "B", "T"][k]; // append power
  return e;
};

const sortAdvisorsByRoleAndTitle = (
  advisors: Forme.AdvisorWithRole[]
): Forme.AdvisorWithRole[] => {
  return [...advisors].sort((a, b) => {
    // Primary wealth advisors first
    if (a.isPrimaryWealthAdvisor && !b.isPrimaryWealthAdvisor) return -1;
    if (!a.isPrimaryWealthAdvisor && b.isPrimaryWealthAdvisor) return 1;

    // Then sort by title priority
    const titlePriority = [
      "sr.",
      "senior",
      "advisor",
      "associate advisor",
      "manager",
    ];
    const getTitlePriority = (title: string | null | undefined) => {
      if (!title) return titlePriority.length;
      const lowerTitle = title.toLowerCase();
      const index = titlePriority.findIndex((keyword) =>
        lowerTitle.includes(keyword)
      );
      return index === -1 ? titlePriority.length : index;
    };
    return getTitlePriority(a.title) - getTitlePriority(b.title);
  });
};

export {
  resetPersistedStores,
  validateEmailFormat,
  advisorMeetingSetup,
  last2DigitYearFormatter,
  currencyFormatter,
  percentFormatter,
  abbreviateCurrency,
  sortAdvisorsByRoleAndTitle,
};
